import React, { useEffect, useRef, useState,useCallback } from "react";
import { connect } from "react-redux";
import {
    DataGrid,
    Column,
    Editing,
    Scrolling,
    Selection,
    Sorting,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { LoadPanel } from 'devextreme-react/load-panel';
import routes from "../../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../../common/Messages";
import { alert, confirm } from 'devextreme/ui/dialog';
import { GetFormatedDateWithTime } from '../../../../../../common/Utils'
import { EMPTY_GUID, WarrantyFlagShipment } from '../../../../../../enum/Enum';
import { collectShipExpirationRejections } from './shipExpirationCheck';

function ManuallySelectLotsForShipment(props) {
    const accessToken = props.oidc.user.access_token;
    const [gridData, setGridData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const position = { of: '#accordion-containe2' };
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [showIndicator, setShowIndicator] = useState(true);
    const [hideOnOutsideClick, setHideOnOutsideClick] = useState(false);
    const [shading, setShading] = useState(true);
    const [showPane, setShowPane] = useState(true);
    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, [setLoadPanelVisible]);
    const gridRef = useRef(null);
    const itemRowId = props.itemRowId;
    const transactionDate = props.formFields?.TRANSACTIONDATE;
    const actualReceivedShippedDateTime = props.formFields?.ACTUALRECEIVEDSHIPPEDDATETIME;
    const ignoreSameDayShipTime = props.profile?.Ignoresamedayshiptime;

    // The grid owns the load state: DevExtreme keeps its load panel up for exactly as long as the
    // store's load promise is pending, and suppresses the "no data" text while it is.
    useEffect(() => {
        let stale = false;
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (!itemRowId) { setGridData([]); return; }
                const response = await fetch(
                    `${window._env_.REACT_APP_APIS}/${routes.IM.GetLocationQuantitySummaryByItemOrLot}?itemId=${itemRowId}&transactionDate=${GetFormatedDateWithTime(transactionDate)}&actualReceivedShippedDateTime=${GetFormatedDateWithTime(actualReceivedShippedDateTime)}&ignoreSameDayShipTime=${ignoreSameDayShipTime}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                if (!response.ok) throw new Error(MESSAGES.ERROR);
                const data = await response.json();
                if (!stale) setGridData(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                if (!stale) setGridData([]);
            } finally {
                // Guarded so a superseded request cannot clear the flag while the current one is
                // still in flight - that is what made the spinner vanish early.
                if (!stale) setIsLoading(false);
            }
        };

        fetchData();
        return () => { stale = true; };
    }, [itemRowId, transactionDate, actualReceivedShippedDateTime, ignoreSameDayShipTime, accessToken]);

    // The grid's own load panel, driven imperatively. beginCustomLoading keeps it up until
    // endCustomLoading is called and suppresses the "no data" text meanwhile, which a controlled
    // <LoadPanel visible={...}> could not do reliably - the widget desynced from its prop.
    useEffect(() => {
        const grid = gridRef.current?.instance?.();
        if (!grid) return;
        if (isLoading) grid.beginCustomLoading(''); else grid.endCustomLoading();
    }, [isLoading]);
    // Shipment expiration / warranty check, lot-level arm - Delphi CheckShipExpiration
    // (uWMD0107.pas:1432-1508), covering WARRANTYFLAG_SHIPMENT 'I' (item expiry) and 'L' (lot expiry).
    // Run as each checkbox is toggled, clearing the tick when the row is refused, as Delphi does.
    // This arm uses the ACTUAL shipped date/time with no transaction-date fallback (uWMD0107.pas:1453).
    const handleSelectionChanged = async (e) => {
        const newlySelected = e.currentSelectedRowKeys?.length
            ? e.selectedRowsData.filter((row) => e.currentSelectedRowKeys.includes(row.rowid))
            : [];

        if (!newlySelected.length) return;

        const shipDate = props.formFields?.ACTUALRECEIVEDSHIPPEDDATETIME;

        // Item expiry first: when the item drives the check, the lot's own date is irrelevant.
        let rejected = await collectShipExpirationRejections({
            rows: newlySelected,
            keyOf: (row) => row.rowid,
            labelOf: (row) => row.lotcode,
            expiryOf: (row) => row.ItemExpiryDate,
            shipDate,
            expectedFlag: WarrantyFlagShipment.UseItemExpirationDate,
        });

        if (!rejected.length) {
            rejected = await collectShipExpirationRejections({
                rows: newlySelected,
                keyOf: (row) => row.rowid,
                labelOf: (row) => row.lotcode,
                expiryOf: (row) => row.ExpiryDate,
                shipDate,
                expectedFlag: WarrantyFlagShipment.UseLotExpirationDate,
            });
        }

        // No Reserve Location class check here: Delphi gates that rule on receipts
        // (uWMD0002.pas:12896) and this screen is shipment-only.
        if (rejected.length)
            e.component.deselectRows(rejected);
    };

    const handleBatchSave = async () => {
        const grid = gridRef.current?.instance();
        await grid.saveEditData();
        const rows = grid.getVisibleRows().map((r) => r.data);
        const selected = grid.getSelectedRowsData();
        if (selected.length == 0) {
            alert(MESSAGES.NO_ROW_SELECTED, MESSAGES.ERROR, 2000);
            return;
        }
        const rowsToSend = selected.length > 0 ? selected : rows;

        if (!rowsToSend.length) {
            alert(MESSAGES.NO_ROW_SELECTED, MESSAGES.ERROR, 2000);
            return;
        }
        const overAvailable = rowsToSend.find(
            (row) => (row.ShippedQuantity ?? 0) > (row.Available ?? 0)
        );
        if (overAvailable) {
            alert(MESSAGES.ACTUAL_QUANTITY_GREATER_THEN_AVAILABLE, MESSAGES.ERROR);
            return;
        }
        // Delphi asks for confirmation when every row was selected (uWMD0107.pas:1184). btnOKClick is
        // shared by both modes of the form, so this applies to the lot level as well as the sublot level.
        if (rows.length > 0 && selected.length === rows.length) {
            const proceed = await confirm(
                `<i>${MESSAGES.ALL_RECORDS_SELECTED_CONFIRM}</i>`,
                MESSAGES.CONFIRM
            );
            if (!proceed) return;
        }
        setLoadPanelVisible(true);
        const payload = rowsToSend.map((row) => {
            const qty = row.ShippedQuantity ?? 0;
            return {
                RowId: props.rTransactionDetail ?? EMPTY_GUID,
                RItem: props.itemRowId,
                RLot: row.RLot,
                QuantityActual: qty,
                RTransactionHeader: props.transactionHeaderId,
                RLocation: row.RLocation,
                RWarehouse: row.RWarehouse,
                IgnoreWarnings: true
            };
        });
        try {
            const response = await fetch(
                `${window._env_.REACT_APP_APIS}/WM/TransactionDetailOperations/ApplyManualLotSelection`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",  
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),  
                }
            );
            setLoadPanelVisible(false);
            if (!response.ok) 
            {
                var error = await response.text()
                alert(error, MESSAGES.ERROR);
                return;
            }
            const result = await response.json();
            if (result.success)
            {
                alert(MESSAGES.CREATE_SUCCESS, MESSAGES.INFORMATION, 2000);
                props.handleManuallySelectLotsForShipmentSave();
            }
            else
            {
                alert(result.message, MESSAGES.ERROR);
            }
        } catch (error) {
            console.error(error.message, error);
            setLoadPanelVisible(false);
        }
    };
    const handleCancel = () => {
        props.CloseManuallySelectLotsForShipmentScreen(false);
    };
    return (
        <div style={{ position: "relative", paddingBottom: "10px" }}>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={showIndicator}
                shading={shading}
                showPane={showPane}
                hideOnOutsideClick={hideOnOutsideClick}
            />
            <DataGrid

                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                id="gridSubLotContainer"
                ref={gridRef}
                dataSource={gridData}
                keyExpr="rowid"
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                height="55vh"
                repaintChangesOnly={true}
                onSelectionChanged={handleSelectionChanged}
            >
                <GridLoadPanel enabled={true} />
                <Scrolling mode="standard" />
                <Selection mode="multiple" />
                <Sorting mode="multiple" />
                <Editing mode="batch" allowUpdating={true} />
                <Column dataField="lotcode" caption="Lot" allowEditing={false} sortIndex={0} sortOrder="asc" />
                <Column dataField="description1" caption="Description" allowEditing={false} />
                <Column dataField="AvailableDate" caption="Available Date" dataType="datetime" format="MM/dd/yyyy hh:mm:ss a" allowEditing={false} />
                <Column dataField="ExpiryDate" caption="Expiry Date" dataType="datetime" format="MM/dd/yyyy" allowEditing={false} />
                <Column dataField="warehouse" caption="Warehouse" allowEditing={false} />
                <Column dataField="LocationCode" caption="Location" allowEditing={false} />
                <Column dataField="Available" caption="Available" dataType="number" format="#,##0.00" allowEditing={false} />
                <Column
                    dataField="ShippedQuantity"
                    caption="Ship Quantity"
                    dataType="number" format="#,##0.00"
                    setCellValue={(newData, value, currentRowData) => {
                        // Delphi warns and then clamps the entry back to Available - it does not just
                        // reject the edit (uWMD0107.pas:1235-1239 for lots, :531-535 for sublots).
                        // The server re-checks as well, since a direct API call bypasses this.
                        if (value > currentRowData.Available) {
                            alert(MESSAGES.ACTUAL_QUANTITY_GREATER_THEN_AVAILABLE, MESSAGES.ERROR);
                            newData.ShippedQuantity = currentRowData.Available;
                            return;
                        }
                        newData.ShippedQuantity = value;
                    }}
                />
                
            </DataGrid>
            <div
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    display: "flex",
                    gap: "10px",
                    zIndex: 1000,
                }}
            >
                <Button
                    text="Ok"
                    type="default"
                    stylingMode="contained"
                    onClick={handleBatchSave}
                    disabled={isLoading || !gridData.length}
                />
                <Button
                    text="Cancel"
                    stylingMode="outlined"
                    onClick={handleCancel}
                />
            </div>

        </div>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ManuallySelectLotsForShipment);
